import { useEffect, useRef, useState } from "react";
import {
  getEventById,
  getQuestionsByEvent,
  getLatestClusters,
  getLatestInsights,
  getEventPublicResponseCount,
  getApprovedLatestManifesto,
} from "../lib/api";
import type { AIInsight, Event, Question } from "../lib/types";

export interface TopCluster {
  label: string;
  size: number;
  question_id: string;
}

export interface UseInsightsResult {
  event: Event | null;
  questions: Question[];
  topClusters: TopCluster[];
  latestInsights: AIInsight[];
  manifesto: AIInsight | null;
  totalResponses: number;
  loading: boolean;
  error: string | null;
}

const POLL_INTERVAL_MS = 5000;

export function useInsights(eventId: string | null): UseInsightsResult {
  const [event, setEvent] = useState<Event | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [topClusters, setTopClusters] = useState<TopCluster[]>([]);
  const [latestInsights, setLatestInsights] = useState<AIInsight[]>([]);
  const [manifesto, setManifesto] = useState<AIInsight | null>(null);
  const [totalResponses, setTotalResponses] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track whether this effect is still mounted so stale ticks don't clobber state
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    if (!eventId) {
      setLoading(false);
      return;
    }

    // New eventId — reset loading so callers can't see stale (event=null, loading=false)
    // between the eventId change and the first fetchAll completing.
    setLoading(true);
    let cancelled = false;

    async function fetchAll() {
      if (!eventId) return;
      try {
        const [ev, qs, count] = await Promise.all([
          getEventById(eventId),
          getQuestionsByEvent(eventId),
          getEventPublicResponseCount(eventId),
        ]);

        // Fetch manifesto in parallel while we still have the event id
        const manifestoData = ev ? await getApprovedLatestManifesto(eventId) : null;

        if (cancelled || !mountedRef.current) return;

        if (!ev) {
          setError("Event nicht gefunden.");
          setLoading(false);
          return;
        }

        // Fetch clusters + insights for all questions in parallel
        const perQuestion = await Promise.all(
          qs.map(async (q) => {
            const [clusters, insights] = await Promise.all([
              getLatestClusters(q.id),
              getLatestInsights(q.id),
            ]);
            return { question: q, clusters, insights };
          }),
        );

        if (cancelled || !mountedRef.current) return;

        // Aggregate: top 3 clusters per question by size, then take top 5 overall
        const allClusters: TopCluster[] = [];
        const allInsights: AIInsight[] = [];

        for (const { clusters, insights, question } of perQuestion) {
          const top3 = clusters
            .slice()
            .sort((a, b) => b.size - a.size)
            .slice(0, 3)
            .map((c) => ({ label: c.label, size: c.size, question_id: question.id }));
          allClusters.push(...top3);
          allInsights.push(...insights);
        }

        const top5 = allClusters.sort((a, b) => b.size - a.size).slice(0, 5);

        setEvent(ev);
        setQuestions(qs);
        setTopClusters(top5);
        setLatestInsights(allInsights);
        setManifesto(manifestoData);
        setTotalResponses(count);
        setError(null);
        setLoading(false);
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        const msg = err instanceof Error ? err.message : "Unbekannter Fehler";
        setError(msg);
        setLoading(false);
        // Keep last good data — do not wipe state on transient poll errors
      }
    }

    // Initial fetch
    fetchAll();

    // Poll every 5 seconds — NO supabase.channel / realtime subscriptions
    const intervalId = setInterval(() => {
      fetchAll();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      mountedRef.current = false;
    };
  }, [eventId]);

  return { event, questions, topClusters, latestInsights, manifesto, totalResponses, loading, error };
}
